import toast from 'react-hot-toast';
import { ExportToCsv } from 'export-to-csv';
import { TableRow, ColumnConfig, ExportFormat } from '../types';
import { Person } from '../../../types/prediction-engine';

export const handleCSVExport = async (
  tableData: TableRow[],
  columns: ColumnConfig[],
  setIsExporting: (loading: boolean) => void,
) => {
  setIsExporting(true);

  try {
    const csvData = tableData.map((row) => {
      const transformedRow: any = {};

      columns.forEach((column) => {
        const key = column.key;
        const value = row[key as keyof TableRow];

        // Handle Person-type columns (actors, directors, writers, producers, stars)
        if (
          key === 'actors' ||
          key === 'directors' ||
          key === 'writers' ||
          key === 'producers' ||
          key === 'stars'
        ) {
          try {
            const persons = value as Person[];

            if (Array.isArray(value)) {
              const names = persons
                .map((person) => person.name || '')
                .join(', ');
              transformedRow[column.header] = names;

              const idsColumnName = `${column.header} IDs`;
              const ids = persons.map((person) => person.id || '').join(', ');
              transformedRow[idsColumnName] = ids;
            } else {
              transformedRow[column.header] = String(value || '');
            }
          } catch {
            transformedRow[column.header] = String(value || '');
          }
        } else if (key.includes('genres')) {
          try {
            const genres = value as string[];
            transformedRow[column.header] = Array.isArray(genres)
              ? genres.join(', ')
              : String(value || '');
          } catch {
            transformedRow[column.header] = String(value || '');
          }
        } else if (key === 'plot') {
          // Handle Plot column - show script.name if script exists, otherwise show plot
          const script = row.script;
          transformedRow[column.header] = script?.name
            ? String(script.name)
            : String(value || '');

          // Add Script column right after Plot with script.id
          transformedRow['Script'] = script?.id ? String(script.id) : '';
        } else {
          transformedRow[column.header] = String(value || '');
        }
      });

      return transformedRow;
    });

    const csvExporter = new ExportToCsv({
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
      filename: `movie_data_${new Date().toISOString().split('T')[0]}`,
    });

    csvExporter.generateCsv(csvData);
    toast.success('CSV export completed!');
  } catch (error) {
    console.error('Export failed:', error);
    toast.error('Failed to export CSV');
  } finally {
    setIsExporting(false);
  }
};

export const handleExport = async (
  format: ExportFormat,
  tableData: TableRow[],
  columns: ColumnConfig[],
  setIsExporting: (loading: boolean) => void,
) => {
  if (format === 'CSV') {
    await handleCSVExport(tableData, columns, setIsExporting);
  }
  // TODO: Add other export formats here (JSON, Excel)
};
